import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";

import { asset } from "@/lib/assets";
const lettering1 = asset("case-studies/lettering/Lettering1.png");
const lettering2 = asset("case-studies/lettering/Lettering2.png");
const lettering3 = asset("case-studies/lettering/Lettering3.jpg");
const lettering4 = asset("case-studies/lettering/Lettering4.png");
const lettering5 = asset("case-studies/lettering/Lettering5.png");
const lettering6 = asset("case-studies/lettering/Lettering6.png");
const lettering7 = asset("case-studies/lettering/Lettering7.png");

// ─── Media slots ────────────────────────────────────────────────────────────
// Add images/videos here. span: "normal" (1 col) | "large" (2 cols)
// Recommended sizes:
//   normal → 900 × 900 px  (1:1)  ~200 KB
//   large  → 1800 × 900 px (2:1)  ~350 KB  ← use sparingly, breaks the square rhythm
//   video  → MP4 720p, poster image same size as slot
type MediaType = "image" | "video";
type SpanType = "normal" | "large";

interface GalleryItem {
  id: number;
  type: MediaType;
  src?: string;
  poster?: string;
  alt: string;
  span: SpanType;
}

const GALLERY_ITEMS: GalleryItem[] = [
  { id: 1, type: "image", src: lettering1, alt: "Lettering 1", span: "large"  },
  { id: 2, type: "image", src: lettering2, alt: "Lettering 2", span: "normal" },
  { id: 3, type: "image", src: lettering3, alt: "Lettering 3", span: "normal" },
  { id: 4, type: "image", src: lettering4, alt: "Lettering 4", span: "normal" },
  { id: 5, type: "image", src: lettering5, alt: "Lettering 5", span: "normal" },
  { id: 6, type: "image", src: lettering6, alt: "Lettering 6", span: "normal" },
  { id: 7, type: "image", src: lettering7, alt: "Lettering 7", span: "normal" },
];

// ─── Lightbox ────────────────────────────────────────────────────────────────
function Lightbox({
  items,
  index,
  onClose,
  onPrev,
  onNext,
}: {
  items: GalleryItem[];
  index: number;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
}) {
  const item = items[index];

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, onPrev, onNext]);

  return createPortal(
    <div
      className="fixed inset-0 bg-black/90 z-[20000] flex items-center justify-center p-4"
      onClick={onClose}
    >
      {/* Close */}
      <button
        className="absolute top-5 right-5 text-white/70 hover:text-white text-3xl leading-none"
        onClick={onClose}
        aria-label="Close"
      >
        ×
      </button>

      {/* Counter */}
      <span className="absolute top-5 left-1/2 -translate-x-1/2 text-white/50 text-sm font-[DM_Sans]">
        {index + 1} / {items.length}
      </span>

      {/* Media */}
      <div
        className="max-w-3xl w-full max-h-[85vh] flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        {item.src ? (
          item.type === "video" ? (
            <video
              src={item.src}
              poster={item.poster}
              controls
              autoPlay
              className="max-w-full max-h-[85vh] rounded-xl"
            />
          ) : (
            <img
              src={item.src}
              alt={item.alt}
              className="max-w-full max-h-[85vh] rounded-xl object-contain"
            />
          )
        ) : (
          <div className="w-full aspect-square bg-white/10 rounded-xl flex items-center justify-center">
            <span className="text-white/30 font-[DM_Sans] text-sm">No media yet</span>
          </div>
        )}
      </div>

      {/* Prev / Next */}
      {index > 0 && (
        <button
          className="absolute left-4 top-1/2 -translate-y-1/2 text-white/60 hover:text-white text-4xl leading-none px-2"
          onClick={(e) => { e.stopPropagation(); onPrev(); }}
          aria-label="Previous"
        >
          ‹
        </button>
      )}
      {index < items.length - 1 && (
        <button
          className="absolute right-4 top-1/2 -translate-y-1/2 text-white/60 hover:text-white text-4xl leading-none px-2"
          onClick={(e) => { e.stopPropagation(); onNext(); }}
          aria-label="Next"
        >
          ›
        </button>
      )}
    </div>,
    document.body
  );
}

// ─── Gallery item card ────────────────────────────────────────────────────────
function GalleryCard({
  item,
  onClick,
}: {
  item: GalleryItem;
  onClick: () => void;
}) {
  const spanClass =
    item.span === "large" ? "col-span-1 md:col-span-2" : "col-span-1";

  return (
    <div className={`${spanClass} group cursor-zoom-in`} onClick={onClick}>
      <div
        className={`relative w-full ${item.span === "large" ? "aspect-[2/1]" : "aspect-square"} rounded-2xl overflow-hidden bg-[#ECEAF8] border border-[#E2E8F0]
                   transition-all duration-300 ease-out
                   group-hover:-translate-y-1 group-hover:shadow-[0_12px_32px_rgba(0,0,0,0.15)]`}
      >
        {item.src ? (
          item.type === "video" ? (
            <video
              src={item.src}
              poster={item.poster}
              muted
              loop
              playsInline
              className="w-full h-full object-cover"
            />
          ) : (
            <img
              src={item.src}
              alt={item.alt}
              className="w-full h-full object-cover"
            />
          )
        ) : (
          /* Placeholder */
          <div className="w-full h-full flex flex-col items-center justify-center gap-2">
            {item.type === "video" ? (
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" className="opacity-25">
                <rect x="2" y="4" width="20" height="16" rx="3" stroke="#1A1A1A" strokeWidth="1.5"/>
                <polygon points="10,9 10,15 16,12" fill="#1A1A1A" opacity="0.5"/>
              </svg>
            ) : (
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" className="opacity-25">
                <rect x="3" y="3" width="18" height="18" rx="3" stroke="#1A1A1A" strokeWidth="1.5"/>
                <circle cx="8.5" cy="8.5" r="1.5" fill="#1A1A1A" opacity="0.5"/>
                <path d="M3 15l5-5 4 4 3-3 6 6" stroke="#1A1A1A" strokeWidth="1.5" strokeLinejoin="round"/>
              </svg>
            )}
            <span className="font-[DM_Sans] text-xs text-[#888] uppercase tracking-widest">
              {item.id}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export function CaseStudyLettering() {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const openLightbox = useCallback((i: number) => setLightboxIndex(i), []);
  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  const prevItem = useCallback(() =>
    setLightboxIndex((i) => (i !== null && i > 0 ? i - 1 : i)), []);
  const nextItem = useCallback(() =>
    setLightboxIndex((i) => (i !== null && i < GALLERY_ITEMS.length - 1 ? i + 1 : i)), []);

  return (
    <div className="min-h-screen bg-[#F5F5F7]">
      {/* Nav */}
      <header className="sticky top-0 z-50 bg-[#F5F5F7]/80 backdrop-blur-md border-b border-[#E2E8F0]">
        <div className="max-w-5xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-2 text-[#888] hover:text-[#1A1A1A] transition-colors font-[DM_Sans] text-sm no-underline"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M19 12H5M5 12l7-7M5 12l7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Back
          </Link>
          <span className="font-[Bricolage_Grotesque] font-medium text-[#1A1A1A] text-sm">
            Hand lettering
          </span>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-16">
        {/* Header */}
        <div className="mb-12">
          <p className="font-[DM_Sans] text-xs uppercase tracking-widest text-[#888] mb-3">
            Paper · Procreate · Illustrator
          </p>
          <h1 className="font-[Bricolage_Grotesque] font-medium text-[clamp(2rem,5vw,3.5rem)] tracking-[-0.03em] text-[#1A1A1A] mb-4">
            Hand lettering
          </h1>
          <p className="font-[DM_Sans] text-[#888] text-lg max-w-xl leading-relaxed">
            Lettering compositions made by me from scratch.
          </p>
        </div>

        {/* Gallery grid — 3 cols, square cells */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {GALLERY_ITEMS.map((item, i) => (
            <GalleryCard
              key={item.id}
              item={item}
              onClick={() => openLightbox(i)}
            />
          ))}
        </div>
      </main>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <Lightbox
          items={GALLERY_ITEMS}
          index={lightboxIndex}
          onClose={closeLightbox}
          onPrev={prevItem}
          onNext={nextItem}
        />
      )}
    </div>
  );
}
